<script lang="ts">
  import { format, isDate, isAfter, addDays } from 'date-fns';
  import { ja } from 'date-fns/locale';
  import RepositoryFactory, { CONTRACT } from '../../../../repositories/RepositoryFactory';
  import { loginUser } from '../../../../components/auth/LoginUser';
  import { isValidDate } from '../../../../components/validator/Validator';
  import { form, field } from 'svelte-forms';
  import { required } from 'svelte-forms/validators';

  const hopeDate = field('hopeDate', '', [required(), isValidDate]);
  const myForm = form(hopeDate);

  type ConfirmedBgColor = 'bg-yellow-100' | 'bg-sky-100';

  let pageStatus: 'INPUT' | 'CONFIRM' | 'COMPLETE' = 'INPUT';

  let constructionCost: string = '';
  let confirmedBgColor: ConfirmedBgColor = 'bg-yellow-100';
  let agreementFlg: boolean = false;

  const isAfterDay = (value: string, addDay: number = 0): boolean => {
    return isAfter(new Date(value), addDays(new Date(), addDay));
  };

  (async () => {
    const contractRepository = RepositoryFactory[CONTRACT];
    contractRepository
      .getContract()
      .then((res) => {
        if (!res) {
          throw new Error();
        }
        constructionCost = getContructionCost(res.serviceCode);
      })
      .catch((error) => {
        displayErrorAlert(error.response.status);
      });
  })();

  const handleTransitionConfirm = async () => {
    // 必須チェック
    let isFormValid = false;
    await myForm.validate().then(() => {
      isFormValid = $myForm.valid;
    });
    if (!isFormValid) {
      return;
    }

    let errorMessage = '';
    // 移設希望日 単項目チェック
    if (!isAfterDay($hopeDate.value, 6)) {
      errorMessage += '移設希望日は7営業日後以降を指定ください\n';
    }

    if (errorMessage) {
      alert(errorMessage);
      return;
    }
    pageStatus = 'CONFIRM';
  };

  const handleTransitionComplete = async () => {
    const contractRepository = RepositoryFactory[CONTRACT];
    await contractRepository
      .requestHikari01Transfer({
        constructionCost,
        hopeDate: $hopeDate.value,
        contentsEntered: getContentsEntered()
      })
      .then((res) => {
        pageStatus = 'COMPLETE';
      })
      .catch((error) => {
        displayErrorAlert(error.response.status);
      });
  };

  const onChangeCheckbox = (): void => {
    confirmedBgColor = agreementFlg ? 'bg-sky-100' : 'bg-yellow-100';
  };

  $: isAgreementChecked = () => {
    return agreementFlg;
  };

  $: getContentsEntered = () => {
    return `・工事費用：${constructionCost}
・移設希望日：${hopeDate}
・移設についての同意事項：チェック済`;
  };

  function displayErrorAlert(status: number) {
    if (status === 403) {
      alert('セッションが切れました。再度ログインしてください。');
      loginUser.logout();
      location.reload();
    } else {
      alert('エラーが発生しました。再度やり直してください。');
    }
  }

  const getContructionCost = (serviceCode: string): string => {
    if (serviceCode === 'HIKARI_01_LINE_SPAAQS_LAN_2') {
      // HIKARI_01_LINE_SPAAQS_LAN_2 は LANを含んでいるが、直収扱いのためDIRECTと同じ金額
      return '63,690円';
    }

    if (serviceCode.includes('VDSL')) {
      return '6,600円';
    } else if (serviceCode.includes('LAN')) {
      return '16,500円';
    } else {
      return '63,690円';
    }
  };
</script>

<div class="h-auto p-6 bg-white m-auto max-w-4xl">
  <div class="mb-6">
    <div class="container flex justify-between">
      <h1 class="text-2xl font-bold text-gray-700">移設(同一建物内の回線設備の移設)</h1>
    </div>
  </div>
  {#if pageStatus === 'INPUT'}<!-- 入力画面 -->
    <div class="shadow rounded bg-white border-t-2 border-green-400">
      <div class="flex-1 m-10">
        <div class="mb:font-sm w-full">
          <p>
            同一建物内の引っ越し等の場合、引っ越し先の部屋等に回線設備を移設することで契約をご継続いただくことが可能です。
          </p>
          <p>以下内容をご確認、入力いただき、「手続きへ」を押下ください</p>
        </div>

        <div class="mb:font-sm w-full mt-10">
          <p>■注意事項</p>
          <ul class="list-disc mt-2 ml-4">
            <li>
              <p>
                移設工事の日程調整が必要となるため、本お手続き後、一週間以内にカスタマーセンターからご登録いただいている電話番号にご連絡させていただきます
              </p>
            </li>
            <li>
              <p>
                移設工事には、以下の工事費用が発生します。原則、工事完了の翌月に、他の月額費用等と合わせて請求させていただきます
              </p>
              <p class="ml-4">工事費用：{constructionCost}</p>
            </li>
          </ul>
        </div>

        <div class="mb:font-sm w-full mt-10">
          <p>■以下を入力ください</p>

          <div class="flex-1 m-2">
            <form>
              <div class="flex flex-col justify-items-center items-center">
                <div class="shadow rounded bg-gray-50 w-10/12 content-center p-2">
                  <div class="inline-grid grid-cols-2 items-center min-w-full p-2">
                    <div class="grid-1 justify-self-start w-full">
                      <span class="form-label-title required">移設希望日</span>
                    </div>
                    <div class="grid-1 justify-self-start w-full">
                      <input
                        type="date"
                        autocomplete="off"
                        bind:value={$hopeDate.value}
                        min={format(new Date(), 'yyyy-MM-dd', { locale: ja })}
                      />
                      {#if $myForm.hasError('hopeDate.required')}
                        <p class="text-red-500 text-xs mt-2">移設希望日を入力してください</p>
                      {/if}
                      {#if $myForm.hasError('hopeDate.invalid_date')}
                        <p class="text-red-500 text-xs mt-2">移設希望日に正しい日付を入力してください</p>
                      {/if}
                    </div>
                  </div>
                  <div class="items-center min-w-full p-2">
                    <ul class="list-none text-xs">
                      <li>
                        <p>※移設希望日は本日より7営業日以降をご指定ください</p>
                        <p class="ml-4">(土日祝日、年末年始等は非営業日となります)</p>
                      </li>
                      <li>
                        <p>※工事受付状況等により工事希望日に添えない場合があります</p>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </form>
          </div>

          <ul class="list-disc mt-10 ml-4">
            <li>
              <p>注意事項等にご同意いただける場合、チェックボックスにチェックをつけてください</p>
            </li>
          </ul>

          <div class="flex flex-col justify-items-center items-center p-2">
            <div class="flex items-center px-4 rounded border border-transparent {confirmedBgColor}">
              <input
                id="agreementFlg"
                type="checkbox"
                class="w-4 h-4 text-red-600"
                bind:checked={agreementFlg}
                on:change={onChangeCheckbox}
              />
              <label for="agreementFlg" class="py-4 ml-2 w-full text-sm font-medium text-red-700"
                >日程調整と移設工事が必要な点について同意します</label
              >
            </div>
          </div>
        </div>
      </div>
      <div class="flex flex-col justify-items-center items-center">
        <div class="flex-1 m-5 flex flex-col md:flex-row justify-items-center items-center">
          <button
            class="button-primary md:ml-5 mb-5"
            disabled={!isAgreementChecked() || !constructionCost}
            on:click={handleTransitionConfirm}
          >
            手続きへ
          </button>
        </div>
      </div>
    </div>
  {:else if pageStatus === 'CONFIRM'}<!-- 確認画面 -->
    <div class="shadow rounded bg-white border-t-2 border-green-400">
      <div class="flex-1 m-10">
        <div class="mb:font-sm w-full">
          <p>以下内容で移設のお手続きを進めます。下記問題なければ実行ボタンを押下ください。</p>
        </div>

        <div class="mb:font-sm w-full mt-10">
          <ul class="list-disc ml-4">
            <li>
              <p>工事費用：{constructionCost}</p>
            </li>
            <li>
              <p>移設希望日：{$hopeDate.value}</p>
            </li>
            <li>
              <p>移設についての同意事項：チェック済</p>
            </li>
          </ul>
        </div>
      </div>
      <div class="flex flex-col justify-items-center items-center">
        <div class="flex-1 m-5 flex flex-col md:flex-row justify-items-center items-center">
          <button class="button-primary md:ml-5 mb-5" on:click={handleTransitionComplete}> 実行 </button>
        </div>
      </div>
    </div>
  {:else if pageStatus === 'COMPLETE'}<!-- 完了画面 -->
    <div class="shadow rounded bg-white border-t-2 border-green-400">
      <div class="flex-1 m-10">
        <div class="inline-flex justify-items-center items-center min-w-full">
          <div class="mb:font-sm w-full mb-10">
            <ul class="list-none">
              <li>
                <p>移設の依頼を受け付けました</p>
              </li>
              <li class="mt-4">
                <p>お手続きについてご不明な点がございましたら、以下からご連絡いただくようお願いいたします。</p>
              </li>
              <li>
                <p>URL：https://bb01.unext.co.jp/contact</p>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  {/if}
</div>

<style lang="postcss">
  p {
    @apply tracking-normal mb-1;
  }
  .form-label-title {
    @apply text-sm font-medium text-gray-700;
  }
  .required:after {
    content: ' *';
    color: red;
  }
  input[type='date'] {
    width: 60%;
    @apply rounded border py-2 px-3 text-sm placeholder-gray-400 focus:placeholder-gray-200 focus:outline-none focus:ring-2 focus:ring-green-200 focus:border-transparent w-full;
  }
  .button-primary {
    @apply bg-green-400 rounded hover:bg-green-500 text-sm font-semibold text-white focus:outline-none px-10 py-2;
  }
  .button-primary:disabled {
    @apply opacity-30 cursor-default;
  }
</style>
